import type { BrainEntityType } from '../brain';
import {
  BRAIN_SIGNAL_LEVEL_ORDER,
  BrainSignalLevel,
  BrainSignalStatus,
} from '../brainSignals';
import {
  QueueMessageType,
  TriggerKey,
  UserCommunicationAgentQueueBodyBase,
} from '../userCommunicationAgent';

export type BrainSignalTriggerKey =
  | TriggerKey.BRAIN_SIGNAL_OPENED
  | TriggerKey.BRAIN_SIGNAL_LEVEL_REACHED
  | TriggerKey.BRAIN_SIGNAL_CHANGED
  | TriggerKey.BRAIN_SIGNAL_CLOSED;

export const BRAIN_SIGNAL_TRIGGER_KEYS: BrainSignalTriggerKey[] = [
  TriggerKey.BRAIN_SIGNAL_OPENED,
  TriggerKey.BRAIN_SIGNAL_LEVEL_REACHED,
  TriggerKey.BRAIN_SIGNAL_CHANGED,
  TriggerKey.BRAIN_SIGNAL_CLOSED,
];

export const BRAIN_SIGNAL_TRIGGER_MAX_DEFINITIONS = 20;

export const BRAIN_SIGNAL_EVENT_SUMMARY_MAX_CHARS = 500;

export interface BrainSignalTriggerConfig {
  definitionIds: string[];
  // Required by LEVEL_REACHED, ignored by the other keys.
  minLevel?: BrainSignalLevel;
}

export interface BrainSignalEventQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.BRAIN_SIGNAL_EVENT;
  // Flow owner, not the member whose change moved the signal.
  userId: string;
  agentId: string;
  teamId: string;
  signalId: string;
  version: number;
  triggerKey: BrainSignalTriggerKey;
  definitionId: string;
  // The definition's name: the queue consumer holds no Brain KMS grant, so it cannot open it.
  signalName: string;
  templateId?: string;
  level: BrainSignalLevel;
  previousLevel: BrainSignalLevel;
  status: BrainSignalStatus;
  scopeEntityId: string;
  scopeEntityType: BrainEntityType;
  contentKey: string;
  // When the signal opened; `at` is the instant of this transition.
  openedAt: string;
  at: string;
  // Present only when the flow owner passed the signal ACL; capped at BRAIN_SIGNAL_EVENT_SUMMARY_MAX_CHARS.
  summary?: string;
  reasonCodes: string[];
  citedAssertionIds: string[];
}

export interface BrainSignalTriggerEvent {
  definitionId: string;
  level: BrainSignalLevel;
  previousLevel: BrainSignalLevel;
  triggerKey: TriggerKey;
}

const levelRank = (level: BrainSignalLevel): number =>
  BRAIN_SIGNAL_LEVEL_ORDER.indexOf(level);

const reachedMinLevel = (
  minLevel: BrainSignalLevel | undefined,
  event: BrainSignalTriggerEvent,
): boolean =>
  minLevel !== undefined &&
  levelRank(event.level) >= levelRank(minLevel) &&
  levelRank(event.previousLevel) < levelRank(minLevel);

// The caller picks the applicable key from the transition and compares it with the flow's own key; this only judges the config.
export const matchesBrainSignalTrigger = (
  config: BrainSignalTriggerConfig | undefined,
  event: BrainSignalTriggerEvent,
): boolean => {
  if (!config?.definitionIds?.includes(event.definitionId)) {
    return false;
  }

  switch (event.triggerKey) {
    case TriggerKey.BRAIN_SIGNAL_OPENED:
    case TriggerKey.BRAIN_SIGNAL_CLOSED:
      return true;
    case TriggerKey.BRAIN_SIGNAL_CHANGED:
      return event.level !== event.previousLevel;
    case TriggerKey.BRAIN_SIGNAL_LEVEL_REACHED:
      return reachedMinLevel(config.minLevel, event);
    default:
      return false;
  }
};

export const BRAIN_SIGNAL_CONFIG_ERROR = {
  DEFINITION_IDS: `triggerConfig.definitionIds must be an array of 1 to ${BRAIN_SIGNAL_TRIGGER_MAX_DEFINITIONS} unique, non-empty strings`,
  MIN_LEVEL: 'triggerConfig.minLevel must be a Brain signal level',
  MIN_LEVEL_REQUIRED:
    'triggerConfig.minLevel is required for triggerKey BRAIN_SIGNAL_LEVEL_REACHED',
  MIN_LEVEL_NONE:
    'triggerConfig.minLevel NONE can never be reached for triggerKey BRAIN_SIGNAL_LEVEL_REACHED',
} as const;

const isUniqueNonEmptyStrings = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.every((item) => typeof item === 'string' && item.length > 0) &&
  new Set(value).size === value.length;

// Shape only: whether the caller may use these definitions is judged where the team is known.
export const findBrainSignalTriggerConfigError = (
  triggerKey: TriggerKey,
  config: unknown,
): string | undefined => {
  const { definitionIds, minLevel } = (config ?? {}) as Record<string, unknown>;

  if (
    !isUniqueNonEmptyStrings(definitionIds) ||
    definitionIds.length === 0 ||
    definitionIds.length > BRAIN_SIGNAL_TRIGGER_MAX_DEFINITIONS
  ) {
    return BRAIN_SIGNAL_CONFIG_ERROR.DEFINITION_IDS;
  }

  if (minLevel === undefined) {
    return triggerKey === TriggerKey.BRAIN_SIGNAL_LEVEL_REACHED
      ? BRAIN_SIGNAL_CONFIG_ERROR.MIN_LEVEL_REQUIRED
      : undefined;
  }

  if (!BRAIN_SIGNAL_LEVEL_ORDER.includes(minLevel as BrainSignalLevel)) {
    return BRAIN_SIGNAL_CONFIG_ERROR.MIN_LEVEL;
  }

  return triggerKey === TriggerKey.BRAIN_SIGNAL_LEVEL_REACHED &&
    minLevel === BrainSignalLevel.NONE
    ? BRAIN_SIGNAL_CONFIG_ERROR.MIN_LEVEL_NONE
    : undefined;
};
